/**
 * Detects whether the app is running inside the Android TWA (Trusted Web Activity).
 *
 * Why? For Play Store compliance under the Organization account policy, payment
 * flows (Escrow, Premium subscribe, Auction deposits) MUST NOT execute inside
 * the Android app. When `useIsTWA()` returns true, payment buttons either hide
 * themselves or redirect to external Chrome via openExternal() so the financial
 * transaction technically happens outside the app.
 *
 * Detection signals (cheapest first):
 *   1. `?utm_source=twa` query param on initial load (set as TWA `startUrl`)
 *      → persisted to sessionStorage for the whole session
 *   2. Android + standalone display mode + referrer starts with android-app://
 */
import { useEffect, useState } from "react";

const SESSION_KEY = "x67_is_in_twa";

function detectTWA() {
  if (typeof window === "undefined") return false;
  try {
    if (sessionStorage.getItem(SESSION_KEY) === "1") return true;
  } catch (_) {}

  const params = new URLSearchParams(window.location.search);
  if (params.get("utm_source") === "twa") {
    try {
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch (_) {}
    return true;
  }

  // Fallback heuristic for Android Chrome standalone mode launched from
  // the TWA shortcut (covers cases where utm_source was stripped).
  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)").matches || false;
  const ua = window.navigator.userAgent || "";
  const isAndroid = /Android/i.test(ua);
  const fromAndroidApp = (document.referrer || "").startsWith("android-app://");

  if (isStandalone && isAndroid && fromAndroidApp) {
    try {
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch (_) {}
    return true;
  }
  return false;
}

export default function useIsTWA() {
  const [isTWA, setIsTWA] = useState(false);

  useEffect(() => {
    setIsTWA(detectTWA());
  }, []);

  return isTWA;
}

/**
 * Force-open a URL in external Chrome (or system browser) — used by payment
 * buttons inside the TWA so the financial transaction happens outside the app.
 *
 * On Android Chrome inside a TWA, `window.open(url, '_blank')` opens a Chrome
 * Custom Tab, which Google treats as external browser navigation.
 */
export function openExternal(path) {
  if (typeof window === "undefined") return;
  // Always use the production domain for external payment flows
  const url = path.startsWith("http")
    ? path
    : `https://x67digital.com${path.startsWith("/") ? "" : "/"}${path}`;
  // Chrome Custom Tab via `_blank` works inside TWAs
  window.open(url, "_blank", "noopener,noreferrer");
}
